<script setup lang="ts">
import type { ManagedUser } from '../internal/types.ts'
import { ActionButton, StatusTag } from '@taler/ui'
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
  subject: string
  isOwnAccount: boolean
  status: ManagedUser | undefined
  isSuspending: boolean
  suspendError: Error | null
  isRestoring: boolean
  restoreError: Error | null
}>()

const emit = defineEmits<{
  suspend: []
  restore: []
}>()

const { t } = useI18n()
const confirming = ref(false)

watch(
  () => props.subject,
  () => (confirming.value = false),
)

function confirmSuspend(): void {
  confirming.value = false
  emit('suspend')
}
</script>

<template>
  <section
    class="flex flex-col gap-[var(--space-block)] rounded-[var(--radius-surface)] border border-[color:var(--color-border)] p-[var(--space-block)] bg-[color:var(--color-surface)]"
  >
    <h2 class="text-[length:var(--text-step-5)] font-semibold text-[color:var(--color-text)]">
      {{ t('identityAdmin.statusTitle') }}
    </h2>

    <StatusTag v-if="props.status === undefined" tone="neutral">
      {{ t('identityAdmin.statusUnknown') }}
    </StatusTag>
    <StatusTag v-else :tone="props.status.banned ? 'danger' : 'ok'">
      {{ props.status.banned ? t('identityAdmin.statusSuspended') : t('identityAdmin.statusActive') }}
    </StatusTag>

    <p
      v-if="props.isOwnAccount"
      class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]"
    >
      {{ t('identityAdmin.ownAccountNote') }} {{ t('identityAdmin.cannotSuspendSelf') }}
    </p>

    <div v-else class="flex flex-col gap-[var(--space-inline)]">
      <template v-if="confirming">
        <p class="font-semibold text-[color:var(--color-text)]">
          {{ t('identityAdmin.suspendConfirmTitle', { subject: props.subject }) }}
        </p>
        <p class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]">
          {{ t('identityAdmin.suspendConfirmBody') }}
        </p>
        <div class="flex gap-[var(--space-inline)]">
          <button
            type="button"
            :disabled="props.isSuspending"
            class="min-h-[var(--density-target-min)] flex-1 rounded-[var(--radius-control)] bg-[color:var(--color-danger)] px-3 py-2 text-[color:var(--color-text-inverse)] disabled:opacity-60"
            @click="confirmSuspend"
          >
            {{ props.isSuspending ? t('identityAdmin.suspending') : t('identityAdmin.suspendConfirmAction') }}
          </button>
          <button
            type="button"
            class="min-h-[var(--density-target-min)] flex-1 rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-3 py-2 text-[color:var(--color-text)]"
            @click="confirming = false"
          >
            {{ t('identityAdmin.suspendCancel') }}
          </button>
        </div>
      </template>
      <button
        v-else
        type="button"
        class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-3 py-2 text-[color:var(--color-text)]"
        @click="confirming = true"
      >
        {{ t('identityAdmin.suspendAction') }}
      </button>

      <StatusTag v-if="props.suspendError" tone="danger" role="alert">
        {{ t('identityAdmin.suspendError') }}
      </StatusTag>
    </div>

    <ActionButton type="button" :disabled="props.isRestoring" @click="emit('restore')">
      {{ props.isRestoring ? t('identityAdmin.restoring') : t('identityAdmin.restoreAction') }}
    </ActionButton>

    <StatusTag v-if="props.restoreError" tone="danger" role="alert">
      {{ t('identityAdmin.restoreError') }}
    </StatusTag>
  </section>
</template>
